import { useMemo, useEffect, useState, useRef } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { Container } from '@/components/container';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetDiscountReportsQuery,
  useLazyGetDiscountReportsCsvQuery,
} from '@/services/DiscountReportsService';
import {
  DiscountUsageFilters,
  DiscountUsageItem,
} from '@/types/discount-reports.types';
import { CouponType } from '@/constant';
import Select, { MultiValue } from 'react-select';
import { useSnackbar } from 'notistack';
import { generateReportPdf } from '@/utils/pdfExport';
import { useGetPromosQuery } from '@/services/PromoManagementService';
import useDebounce from '@/hooks/useDebounce';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/Date';

const numberFormat = (n: number) => new Intl.NumberFormat().format(n);
type SelectOption = { value: string; label: string };

const DiscountTrackingReports = () => {
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const summaryRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  // Filters could be extended (date range, coupon_code)
  const couponTypeOptions: SelectOption[] = [
    { value: String(CouponType.SUBSCRIPTION_BOATER), label: 'Boater' },
    { value: String(CouponType.SUBSCRIPTION_HARBOR), label: 'Harbor' },
    { value: String(CouponType.SUBSCRIPTION_CLUB), label: 'Club' },
    { value: String(CouponType.SUBSCRIPTION_DMO), label: 'Organization' },
    {
      value: String(CouponType.SUBSCRIPTION_SERVICE_PROVIDER),
      label: 'Subscription',
    },
  ];
  const [selectedCouponTypes, setSelectedCouponTypes] = useState<
    SelectOption[]
  >([]);
  const [getCsv, { isLoading: csvLoading }] =
    useLazyGetDiscountReportsCsvQuery();

  // Coupon code filter (multi-select) with server search
  const [selectedCoupons, setSelectedCoupons] = useState<SelectOption[]>([]);
  const {
    debouncedValue: debouncedCouponSearch,
    searchTerm: couponSearchTerm,
    handleSearch: handleCouponSearch,
  } = useDebounce(300);
  const { data: couponsResponse, isFetching: isCouponsFetching } =
    useGetPromosQuery(
      {
        page: 1,
        limit: 20,
        search: debouncedCouponSearch,
      }
      // do not skip; we want initial options even without input
    );
  const couponOptions: SelectOption[] = useMemo(
    () =>
      (couponsResponse?.data || []).map((p) => ({
        value: p.code,
        label: `${p.code} – ${p.name}`,
      })),
    [couponsResponse]
  );

  // Custom date range selection
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const handleExportPdf = async () => {
    try {
      setPdfLoading(true);
      const date = new Date().toISOString().slice(0, 10);
      await generateReportPdf({
        filename: `discount_tracking_${date}.pdf`,
        title: 'Discount Tracking Report',
        subtitle: `Generated on ${formatDisplayDate(new Date())}`,
        tableElement: tableRef.current || undefined,
        orientation: 'landscape',
        tableData: {
          headers: [
            'User',
            'Email',
            'Coupon',
            'Discount',
            'Order Total (Before)',
            'Order Total (After)',
            'Used At',
          ],
          rows: (rows || []).map((r) => [
            r.user_name,
            r.user_email,
            r.coupon_code,
            formatFinnishCurrency(r.discount_amount),
            formatFinnishCurrency(r.order_total_before),
            formatFinnishCurrency(r.order_total_after),
            formatDisplayDateTime(new Date(r.used_at)),
          ]),
        },
      });
      enqueueSnackbar('PDF exported successfully', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const listParams: DiscountUsageFilters = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    coupon_types: selectedCouponTypes.map((o) => Number(o.value)),
    coupon_codes:
      selectedCoupons.length > 0
        ? selectedCoupons.map((o) => o.value)
        : undefined,
    from:
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid()
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
    to:
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid()
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
  };

  const {
    data: report,
    isLoading,
    isFetching,
    error,
  } = useGetDiscountReportsQuery(listParams);
  const summary = report?.summary;
  const rows: DiscountUsageItem[] = useMemo(
    () => report?.usage.items ?? [],
    [report]
  );

  useEffect(() => {
    if (report) {
      handleTotalPages(report.usage.totalPages || 1);
      handleTotalItems(report.usage.totalItems || 0);
    }
  }, [report, handleTotalItems, handleTotalPages]);

  useEffect(() => {
    if (error) {
      const err = error as unknown as { data?: unknown; error?: string };
      let message = 'Failed to fetch discount reports';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const handleExportCsv = async () => {
    try {
      const blob = await getCsv({
        ...listParams,
        isCsv: true,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `discount_reports_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('CSV exported successfully', { variant: 'success' });
    } catch (e) {
      let message = 'Failed to export CSV';
      if (e && typeof e === 'object') {
        const errObj = e as { data?: unknown; error?: string };
        if (
          errObj.data &&
          typeof errObj.data === 'object' &&
          'message' in (errObj.data as Record<string, unknown>)
        ) {
          const m = (errObj.data as { message?: string }).message;
          if (typeof m === 'string' && m) message = m;
        } else if (typeof errObj.error === 'string' && errObj.error) {
          message = errObj.error;
        }
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const resetFilters = () => {
    if (searchTerm) resetSearch();
    setSelectedCouponTypes([]);
    setSelectedCoupons([]);
    setTempStartDate('');
    setTempEndDate('');
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  // When filters change, go back to first page
  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCouponTypes, selectedCoupons, tempStartDate, tempEndDate]);

  const revenueImpact = useMemo(() => {
    const before = summary?.revenue_before_discount ?? 0;
    const after = summary?.revenue_after_discount ?? 0;
    const delta = before - after;
    const pct = before > 0 ? (delta / before) * 100 : 0;
    return { before, after, delta, pct };
  }, [summary]);

  const columns = [
    {
      accessorFn: (row: DiscountUsageItem) => row.user_name,
      id: 'user',
      header: 'User',
      enableSorting: false,
      cell: ({ row }: { row: { original: DiscountUsageItem } }) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {row.original.user_name}
          </span>
          <span className="text-xs text-gray-500">
            {row.original.user_email}
          </span>
        </div>
      ),
    },
    {
      accessorFn: (row: DiscountUsageItem) => row.coupon_code,
      id: 'coupon',
      header: 'Coupon',
      enableSorting: false,
    },
    {
      accessorFn: (row: DiscountUsageItem) => row.discount_amount,
      id: 'discount',
      header: 'Discount',
      enableSorting: false,
      cell: ({ row }: { row: { original: DiscountUsageItem } }) => (
        <span>{formatFinnishCurrency(row.original.discount_amount)}</span>
      ),
    },
    {
      accessorFn: (row: DiscountUsageItem) => row.order_total_before,
      id: 'before',
      header: 'Order Total (Before)',
      enableSorting: false,
      cell: ({ row }: { row: { original: DiscountUsageItem } }) => (
        <span>{formatFinnishCurrency(row.original.order_total_before)}</span>
      ),
    },
    {
      accessorFn: (row: DiscountUsageItem) => row.order_total_after,
      id: 'after',
      header: 'Order Total (After)',
      enableSorting: false,
      cell: ({ row }: { row: { original: DiscountUsageItem } }) => (
        <span>{formatFinnishCurrency(row.original.order_total_after)}</span>
      ),
    },
    {
      accessorFn: (row: DiscountUsageItem) => row.used_at,
      id: 'used_at',
      header: 'Used At',
      enableSorting: false,
      cell: ({ row }: { row: { original: DiscountUsageItem } }) => (
        <span className="text-sm">
          {formatDisplayDateTime(new Date(row.original.used_at))}
        </span>
      ),
    },
  ];

  return (
    <Container>
      {hasPermission('coupon_management_read') && (
        <>
          <div className="flex items-center justify-between mb-6">
            <h1
              id="discount-tracking-reports-heading"
              className="text-xl font-semibold"
              tabIndex={-1}
            >
              Discount Tracking Reports
            </h1>
            <div className="flex flex-wrap gap-3 sm:gap-5 justify-start sm:justify-end">
              <div className="min-w-[220px]">
                <Select
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="Filter coupon types"
                  options={couponTypeOptions}
                  value={selectedCouponTypes}
                  onChange={(vals: MultiValue<SelectOption>) =>
                    setSelectedCouponTypes(vals as SelectOption[])
                  }
                  aria-label="Filter coupon types"
                />
              </div>
              <div className="min-w-[260px]">
                <Select
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="Search & select coupons"
                  options={couponOptions}
                  value={selectedCoupons}
                  onChange={(vals: MultiValue<SelectOption>) =>
                    setSelectedCoupons(vals as SelectOption[])
                  }
                  onInputChange={(input) => {
                    handleCouponSearch(null, input);
                    return input;
                  }}
                  isLoading={isCouponsFetching}
                  noOptionsMessage={() =>
                    couponSearchTerm
                      ? 'No coupons found'
                      : 'Type to search coupons'
                  }
                  aria-label="Search & select coupons"
                />
              </div>
              <div className="flex-shrink-0">
                <DateRangePicker
                  startDateName="from"
                  endDateName="to"
                  isMandatory={false}
                  value={[tempStartDate, tempEndDate]}
                  onChange={([newStartDate, newEndDate]) => {
                    handleDateRangeChange(
                      newStartDate as string,
                      newEndDate as string
                    );
                  }}
                  allowPastDates={true}
                  allowFutureDates={false}
                  className="w-full sm:w-64 h-8 sm:h-10 text-sm"
                />
              </div>
              {(!!searchTerm ||
                selectedCouponTypes.length > 0 ||
                selectedCoupons.length > 0 ||
                !!tempStartDate ||
                !!tempEndDate) && (
                <div className="flex items-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-light flex items-center gap-2"
                    onClick={resetFilters}
                    title="Clear filters"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleExportPdf}
                  disabled={pdfLoading}
                >
                  {pdfLoading ? 'Exporting...' : 'Export PDF'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleExportCsv}
                  disabled={csvLoading}
                >
                  {csvLoading ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <div className="card-body" ref={summaryRef}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="bg-gray-100 rounded-lg shadow p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">
                        Users Applied Discount
                      </p>
                      <p className="text-2xl font-semibold">
                        {isLoading || isFetching
                          ? '--'
                          : numberFormat(summary?.total_users_applied ?? 0)}
                      </p>
                    </div>
                    <KeenIcon icon="user" className="text-2xl text-primary" />
                  </div>
                </div>

                <div className="bg-gray-100 rounded-lg shadow p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">
                        Subscriptions With Discount
                      </p>
                      <p className="text-2xl font-semibold">
                        {isLoading || isFetching
                          ? '--'
                          : numberFormat(
                              summary?.total_orders_with_discount ?? 0
                            )}
                      </p>
                    </div>
                    <KeenIcon
                      icon="percentage"
                      className="text-2xl text-primary"
                    />
                  </div>
                </div>

                <div className="bg-gray-100 rounded-lg shadow p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">
                        Total Discount Granted
                      </p>
                      <p className="text-2xl font-semibold">
                        {isLoading || isFetching
                          ? '--'
                          : formatFinnishCurrency(
                              summary?.total_discount_amount ?? 0
                            )}
                      </p>
                    </div>
                    <KeenIcon
                      icon="chart-line"
                      className="text-2xl text-primary"
                    />
                  </div>
                </div>

                <div className="bg-gray-100 rounded-lg shadow p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">
                        Revenue (Before Discount)
                      </p>
                      <p className="text-2xl font-semibold">
                        {isLoading || isFetching
                          ? '--'
                          : formatFinnishCurrency(revenueImpact.before)}
                      </p>
                    </div>
                    <KeenIcon icon="chart-line" className="text-2xl text-primary" />
                  </div>
                </div>

                <div className="bg-gray-100 rounded-lg shadow p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">
                        Revenue (After Discount)
                      </p>
                      <p className="text-2xl font-semibold">
                        {isLoading || isFetching
                          ? '--'
                          : formatFinnishCurrency(revenueImpact.after)}
                      </p>
                    </div>
                    <KeenIcon icon="chart-line" className="text-2xl text-primary" />
                  </div>
                </div>

                <div className="bg-gray-100 rounded-lg shadow p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Revenue Impact</p>
                      <p className="text-2xl font-semibold">
                        {isLoading || isFetching
                          ? '--'
                          : `${formatFinnishCurrency(revenueImpact.delta)} (${revenueImpact.pct.toFixed(2)}%)`}
                      </p>
                    </div>
                    <KeenIcon icon="chart" className="text-2xl text-primary" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body" ref={tableRef}>
              <DataGrid
                columns={columns}
                data={rows}
                rowSelect={false}
                loading={isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems,
                  totalPages,
                  setPagination,
                  info: '{from} - {to} of {count}',
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'used_at', desc: true }]}
                serverSide={true}
                onFilterReset={resetFilters}
                hasActiveFilters={
                  !!searchTerm ||
                  selectedCouponTypes.length > 0 ||
                  selectedCoupons.length > 0 ||
                  !!tempStartDate ||
                  !!tempEndDate
                }
                aria-label="Discount usages table"
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default DiscountTrackingReports;
